import { Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, tap } from 'rxjs';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { FormBuilder, FormGroupDirective, ReactiveFormsModule } from '@angular/forms';
import { format as formatDate } from 'date-fns-tz';
import { ActivityTaskType, ActivityTaskTypeTranslationKeys } from '../activity.interface';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TextFieldModule } from '@angular/cdk/text-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FieldValueInterface } from '@vendasta/crm';
import { CrmFieldService, StandardIds } from '../../../../shared-services';
import { DEFAULT_TASK_STATUS, STANDARD_DATE_FORMAT } from './constants';
import { TranslationModule } from '../../../../i18n';

@Component({
  selector: 'crm-activity-logger-follow-up-task',
  imports: [
    CommonModule,
    TranslateModule,
    TranslationModule,
    GalaxyFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    TextFieldModule,
    MatDatepickerModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
  ],
  templateUrl: './follow-up-task.component.html',
  styleUrls: ['./follow-up-task.component.scss'],
})
export class CrmActivityLoggerFollowUpTaskComponent implements OnDestroy {
  @Input({ transform: coerceBooleanProperty }) mobile: boolean | null = null;
  @ViewChild('form') formGroupDirective!: FormGroupDirective;

  @Output() expandedChanged = new EventEmitter<boolean>();
  private isExpanded$$ = new BehaviorSubject<boolean>(false);
  isExpanded$ = this.isExpanded$$.asObservable().pipe(tap((isExpanded) => this.expandedChanged.emit(isExpanded)));

  private today = new Date();
  formGroup = new FormBuilder().nonNullable.group({
    taskDueDate: formatDate(this.today.setDate(this.today.getDate() + 3), STANDARD_DATE_FORMAT),
    taskType: ActivityTaskType.ToDo,
  });

  protected readonly taskTypes = ActivityTaskTypeTranslationKeys;

  constructor(
    private readonly fieldsService: CrmFieldService,
    private readonly translateService: TranslateService,
  ) {}

  toggleExpanded(isExpanded: boolean) {
    this.isExpanded$$.next(isExpanded);
  }

  getValues(crmObjectName: string): FieldValueInterface[] {
    const formValues = this.formGroup.getRawValue();
    const followUpWith = this.translateService.instant('ACTIVITY_LOGGER.LOGGED_FOLLOW_UP_TASK.FOLLOW_UP_WITH');
    return [
      {
        fieldId: this.fieldsService.getFieldId(StandardIds.ActivityName),
        stringValue: `${followUpWith} ${crmObjectName}`,
      },
      {
        fieldId: this.fieldsService.getFieldId(StandardIds.ActivityTaskDueDate),
        dateValue: formValues.taskDueDate ? new Date(formValues.taskDueDate) : undefined,
      },
      {
        fieldId: this.fieldsService.getFieldId(StandardIds.ActivityTaskType),
        stringValue: formValues.taskType,
      },
      {
        fieldId: this.fieldsService.getFieldId(StandardIds.ActivityTaskStatus),
        stringValue: DEFAULT_TASK_STATUS,
      },
    ] as FieldValueInterface[];
  }

  ngOnDestroy(): void {
    this.expandedChanged.emit(false);
  }
}
